<script setup lang="ts">
import { useTranslation } from 'i18next-vue';
import { onMounted, onUnmounted, ref } from 'vue';

import { UiDialogShell } from '@peanuds/ui';

import smallScreenDialogDecor from '../assets/small-screen-dialog-decor-decorpng.png';
import smallScreenDialogIllustration from '../assets/small-screen-dialog-illustration-illustrationpng.png';

defineOptions({
  name: 'SmallScreenDialog',
});

const NORMAL_BREAKPOINT_MEDIA_QUERY = '(min-width: 65rem)';
const isDevEnvironment = import.meta.env.DEV && import.meta.env.MODE !== 'test';

const { t } = useTranslation();

const isVisible = ref(false);

let mediaQueryList: MediaQueryList | null = null;

const syncDialogVisibility = () => {
  if (isDevEnvironment) {
    isVisible.value = false;

    return;
  }

  if (!mediaQueryList) {
    return;
  }

  isVisible.value = !mediaQueryList.matches;
};

onMounted(() => {
  if (isDevEnvironment) {
    return;
  }

  mediaQueryList = window.matchMedia(NORMAL_BREAKPOINT_MEDIA_QUERY);

  syncDialogVisibility();
  window.addEventListener('resize', syncDialogVisibility);
});

onUnmounted(() => {
  if (isDevEnvironment) {
    return;
  }

  window.removeEventListener('resize', syncDialogVisibility);
});
</script>

<template>
  <UiDialogShell
    :visible="isVisible"
    width="23.375rem"
    max-width="calc(100vw - 2rem)"
    surface="plain"
    surface-class="small-screen-dialog__surface"
    :show-pattern="false"
    :dismissable-mask="false"
    :close-on-escape="false"
    labelled-by="small-screen-dialog-title"
    :pt="{
      mask: {
        class: 'small-screen-dialog__mask',
      },
    }"
  >
    <template #surface>
      <div class="small-screen-dialog__container">
        <img
          :src="smallScreenDialogDecor"
          alt=""
          aria-hidden="true"
          class="small-screen-dialog__decor"
        />

        <div class="small-screen-dialog__content">
          <div class="small-screen-dialog__text">
            <h2 id="small-screen-dialog-title" class="small-screen-dialog__title">
              {{ t('small_screen_dialog.title') }}
            </h2>
            <p class="small-screen-dialog__copy">{{ t('small_screen_dialog.copy') }}</p>
          </div>

          <div class="small-screen-dialog__illustration-frame">
            <img
              :src="smallScreenDialogIllustration"
              alt=""
              aria-hidden="true"
              class="small-screen-dialog__illustration"
            />
          </div>
        </div>
      </div>
    </template>
  </UiDialogShell>
</template>

<style scoped lang="scss">
:global(.small-screen-dialog__mask) {
  padding: $space-md;
  background: rgba(15, 23, 42, 0.32) !important;
  -webkit-backdrop-filter: blur(0.5rem) saturate(1.8) brightness(0.82);
  backdrop-filter: blur(0.5rem) saturate(1.8) brightness(0.82);
  animation: small-screen-dialog-mask-fade-in 220ms ease-out both;
}

@keyframes small-screen-dialog-mask-fade-in {
  from {
    background: rgba(15, 23, 42, 0);
    -webkit-backdrop-filter: blur(0) saturate(1) brightness(1);
    backdrop-filter: blur(0) saturate(1) brightness(1);
  }

  to {
    background: rgba(15, 23, 42, 0.32);
    -webkit-backdrop-filter: blur(0.125rem) saturate(1.8) brightness(0.82);
    backdrop-filter: blur(0.125rem) saturate(1.8) brightness(0.82);
  }
}

:global(.ui-dialog-shell__surface.small-screen-dialog__surface) {
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 1rem;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.94) 0%, rgba(255, 255, 255, 0.88) 100%);
  box-shadow:
    0 1rem 2.5rem rgba(148, 163, 184, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.56);
  -webkit-backdrop-filter: blur(1.25rem) saturate(1.2);
  backdrop-filter: blur(1.25rem) saturate(1.2);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    border-radius: inherit;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0) 36%);
    pointer-events: none;
  }
}

.small-screen-dialog {
  &__container {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1.5rem;
    width: 100%;
    padding: 1rem;
  }

  &__decor {
    position: absolute;
    top: 0;
    left: 50%;
    z-index: 0;
    width: calc(100% + 6rem);
    max-width: none;
    opacity: 0.42;
    mix-blend-mode: screen;
    transform: translateX(-50%);
    pointer-events: none;
  }

  &__content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;
  }

  &__text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    text-align: center;
  }

  &__title {
    margin: 0;
    width: min(100%, 20.25rem);
    color: #334155;
    font-size: 1.5rem;
    font-weight: $font-semibold;
    line-height: 1.3;
    letter-spacing: -0.004em;
    text-align: center;
  }

  &__copy {
    margin: 0;
    width: min(100%, 21.375rem);
    color: #64748b;
    font-size: 0.875rem;
    font-weight: $font-regular;
    line-height: 1;
    letter-spacing: -0.004em;
    text-align: center;
  }

  &__illustration-frame {
    width: 100%;
    height: 20.75rem;
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 1.43076925rem;
    background: linear-gradient(180deg, #f5f3ff 0%, #b2adcd 100%);
    box-shadow: 0 0.75rem 1.5rem rgba(168, 139, 255, 0.14);
    overflow: hidden;
  }

  &__illustration {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
</style>
